import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { can, Permission, ROLE_DESCRIPTION } from '../auth/roles';
import { applyTheme, loadTheme, ThemeChoice } from '../lib/theme';
import { RoleBadge } from './ui/Badge';

interface NavItem { to: string; label: string; icon: string; perm: Permission }

export const NAV: { group: string; items: NavItem[] }[] = [
  {
    group: 'Insights',
    items: [
      { to: '/', label: 'Overview', icon: '◎', perm: 'stats:view' },
      { to: '/stats/growth', label: 'Growth & login', icon: '↗', perm: 'stats:view' },
      { to: '/stats/sharing', label: 'Sharing', icon: '🪔', perm: 'stats:view' },
      { to: '/stats/engagement', label: 'Engagement', icon: '📿', perm: 'stats:view' },
    ],
  },
  {
    group: 'Operations',
    items: [
      { to: '/moderation', label: 'Moderation', icon: '⚑', perm: 'moderation:manage' },
      { to: '/users', label: 'Users & access', icon: '👤', perm: 'users:view' },
    ],
  },
  {
    group: 'Configuration',
    items: [
      { to: '/catalog', label: 'Diya catalogue', icon: '✦', perm: 'catalog:manage' },
      { to: '/tulsi', label: 'Tulsi catalogue', icon: '❀', perm: 'catalog:manage' },
      { to: '/mandir', label: 'Mandir catalogue', icon: '🛕', perm: 'catalog:manage' },
      { to: '/content', label: 'Content & festivals', icon: '♪', perm: 'content:manage' },
      { to: '/points', label: 'Points rules', icon: '★', perm: 'points:manage' },
      { to: '/push', label: 'Push notifications', icon: '🔔', perm: 'push:manage' },
      { to: '/settings', label: 'Settings', icon: '⚙', perm: 'settings:manage' },
    ],
  },
];

export function Layout() {
  const { session, logout } = useAuth();
  const [theme, setTheme] = useState<ThemeChoice>(loadTheme);
  const [menuOpen, setMenuOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => applyTheme(theme), [theme]);
  useEffect(() => setMenuOpen(false), [loc.pathname]);
  const role = session?.role;

  return (
    <div className="shell">
      <aside className={`sidebar ${menuOpen ? 'open' : ''}`}>
        <div className="brand">
          <img src="/logo.png" alt="" width={36} height={36} style={{ borderRadius: '50%', flexShrink: 0 }} />
          <div>
            Mera Diya
            <small>Admin console</small>
          </div>
        </div>
        {NAV.map((g) => {
          const items = g.items.filter((i) => can(role, i.perm));
          if (!items.length) return null;
          return (
            <nav className="nav-group" key={g.group} aria-label={g.group}>
              <span>{g.group}</span>
              {items.map((i) => (
                <NavLink key={i.to} to={i.to} end={i.to === '/'} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                  <span className="ico" aria-hidden>
                    {i.icon}
                  </span>
                  {i.label}
                </NavLink>
              ))}
            </nav>
          );
        })}
        {role && (
          <div style={{ marginTop: 'auto', padding: '0 10px', color: 'var(--muted)', fontSize: 12 }}>
            <RoleBadge role={role} />
            <p style={{ margin: '6px 0 0' }}>{ROLE_DESCRIPTION[role]}</p>
          </div>
        )}
      </aside>
      <div className="main">
        <header className="topbar">
          <button className="btn ghost menu-btn" onClick={() => setMenuOpen((o) => !o)} aria-label="Menu">
            ☰
          </button>
          {session?.mode === 'dev' && <span className="badge warning">dev login</span>}
          <select className="select" style={{ width: 'auto' }} value={theme} onChange={(e) => setTheme(e.target.value as ThemeChoice)} aria-label="Theme">
            <option value="system">System theme</option>
            <option value="light">Light</option>
            <option value="dark">Dark</option>
          </select>
          <span style={{ color: 'var(--text-2)' }}>{session?.name ?? session?.userId.slice(0, 8)}</span>
          {role && <RoleBadge role={role} />}
          <button className="btn sm" onClick={() => void logout()}>
            Sign out
          </button>
        </header>
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
